import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, memo, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { SortOrder, ITEM_MIN_HEIGHT_WITH_BORDER, ITEM_MIN_HEIGHT } from '../../Components/SelectionList';
import { List, Search } from './components';
import { DEFAULT_LOAD_DEBOUNCE, DEFAULT_OVERFLOW_LIMIT, DEFAULT_SEARCH_LIMIT, INLINE_VALUE_DELIMITER, DEFAULT_TRANSLATIONS, COMMA, } from './constants';
import { filterOptionsByString, areOptionsEqual, sortOptions, createOptionValueSet, filterOptionsExcluding, getOptionText, } from './helpers';
import { StyledTextarea, Wrapper } from './styled';
const _MultiSelection = ({ selectedValue, onChange, options: staticOptions = [], loadOptions, loadOptionsLimit = DEFAULT_SEARCH_LIMIT, isLoading, disabled, error, initialSelectionSortOrder = SortOrder.ASC, initialSelectedSortOrder = SortOrder.ASC, overflowLimit = DEFAULT_OVERFLOW_LIMIT, getOptionKey, formatOptionLabel, getOptionText: getOptionTextFn, translations, hideInlineToggle = false, hideBulkSelect = false, hideBulkDeselect = false, maxItems, reverseSelectionMode = false, dataTestId, }, ref) => {
    const [fetchedOptions, setFetchedOptions] = useState([]);
    const [internalLoading, setInternalLoading] = useState(!!loadOptions);
    const [selectionFilter, setSelectionFilter] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('');
    const [isInlineMode, setIsInlineMode] = useState(false);
    const [inlineValue, setInlineValue] = useState('');
    const [selectionSortOrder, setSelectionSortOrder] = useState(initialSelectionSortOrder);
    const [selectedSortOrder, setSelectedSortOrder] = useState(initialSelectedSortOrder);
    const selectionListRef = useRef(null);
    const [textareaHeight, setTextareaHeight] = useState(0);
    const listContainerHeight = useMemo(() => ITEM_MIN_HEIGHT_WITH_BORDER * overflowLimit + ITEM_MIN_HEIGHT / 2, [overflowLimit]);
    const mergedTranslations = useMemo(() => ({
        ...DEFAULT_TRANSLATIONS,
        ...translations,
    }), [translations]);
    const hasLoadOptions = useMemo(() => !!loadOptions, [loadOptions]);
    const options = useMemo(() => (hasLoadOptions ? fetchedOptions : staticOptions), [fetchedOptions, staticOptions, hasLoadOptions]);
    const isSelectionFilterDisabled = useMemo(() => (hasLoadOptions ? disabled : isLoading || disabled), [hasLoadOptions, isLoading, disabled]);
    const availableSelectionItems = useMemo(() => {
        const selectedValueSet = createOptionValueSet(selectedValue ?? []);
        return filterOptionsExcluding(options, selectedValueSet);
    }, [options, selectedValue]);
    const filteredSelectionItems = useMemo(() => {
        if (isInlineMode || hasLoadOptions) {
            // For async options, don't apply client-side sorting - server handles it
            return availableSelectionItems;
        }
        const filteredItems = filterOptionsByString(availableSelectionItems, selectionFilter, formatOptionLabel, getOptionTextFn);
        return sortOptions(filteredItems, selectionSortOrder, formatOptionLabel, getOptionTextFn);
    }, [isInlineMode, hasLoadOptions, availableSelectionItems, selectionFilter, formatOptionLabel, selectionSortOrder, getOptionTextFn]);
    const selectableSelectionItems = useMemo(() => filteredSelectionItems.filter((option) => !option.disabled), [filteredSelectionItems]);
    const filteredSelectedItems = useMemo(() => {
        const filteredItems = filterOptionsByString(selectedValue, selectedFilter, formatOptionLabel, getOptionTextFn);
        return sortOptions(filteredItems, selectedSortOrder, formatOptionLabel, getOptionTextFn);
    }, [selectedValue, selectedFilter, formatOptionLabel, selectedSortOrder, getOptionTextFn]);
    const loadOptionsDebounced = useDebouncedCallback(async (searchString, newSortOrder) => {
        if (!loadOptions)
            return;
        setInternalLoading(true);
        try {
            const options = await loadOptions(searchString, loadOptionsLimit.MIN, newSortOrder ?? selectionSortOrder);
            setFetchedOptions(options || []);
        }
        catch {
            setFetchedOptions([]);
        }
        finally {
            setInternalLoading(false);
        }
    }, DEFAULT_LOAD_DEBOUNCE);
    const handleSelectionFilterChange = useCallbackRef((value) => {
        setSelectionFilter(value);
        if (hasLoadOptions) {
            loadOptionsDebounced(value);
        }
    });
    const handleSelectedFilterChange = useCallbackRef((value) => {
        setSelectedFilter(value);
    });
    const clearSelectionFilter = useCallbackRef(() => {
        setSelectionFilter('');
        if (hasLoadOptions) {
            loadOptionsDebounced('');
        }
    });
    const clearSelectedFilter = useCallbackRef(() => {
        setSelectedFilter('');
    });
    const toggleInlineMode = useCallbackRef(() => {
        if (!isInlineMode && selectionListRef.current) {
            setTextareaHeight(selectionListRef.current.offsetHeight);
        }
        setIsInlineMode((prev) => !prev);
        setInlineValue(selectedValue.map((option) => getOptionText(option, formatOptionLabel, getOptionTextFn)).join(INLINE_VALUE_DELIMITER));
    });
    const handleChangeStringValue = useCallbackRef((e) => {
        const { value } = e.currentTarget;
        const optionsMap = new Map(options.map((option) => [getOptionText(option, formatOptionLabel, getOptionTextFn).trim().toLowerCase(), option]));
        const seenValues = new Set();
        const newSelectedOptions = value
            .split(COMMA)
            .map((label) => label.trim())
            .filter((label) => label.length > 0)
            .reduce((acc, label) => {
            const option = optionsMap.get(label.toLowerCase());
            if (option && !seenValues.has(option.value)) {
                seenValues.add(option.value);
                acc.push(option);
            }
            return acc;
        }, []);
        onChange(newSelectedOptions);
        setInlineValue(value);
    });
    const isMaxReached = maxItems !== undefined && selectedValue.length >= maxItems;
    const selectItem = useCallbackRef((option) => {
        if (isMaxReached)
            return;
        onChange([...selectedValue, option]);
    });
    const unselectItem = useCallbackRef((option) => {
        const filteredValue = selectedValue.filter((value) => !areOptionsEqual(option, value));
        if (isInlineMode) {
            setInlineValue(filteredValue.map((option) => getOptionText(option, formatOptionLabel, getOptionTextFn)).join(INLINE_VALUE_DELIMITER));
        }
        onChange(filteredValue);
    });
    const selectAll = useCallbackRef(() => {
        const itemsToAdd = maxItems !== undefined ? selectableSelectionItems.slice(0, maxItems - selectedValue.length) : selectableSelectionItems;
        onChange([...selectedValue, ...itemsToAdd]);
    });
    const unselectAll = useCallbackRef(() => {
        setInlineValue('');
        const itemsOutsideFilter = selectedValue.filter((option) => !filteredSelectedItems.some((value) => areOptionsEqual(option, value)));
        onChange(itemsOutsideFilter);
    });
    const toggleSelectionSortOrder = useCallbackRef(() => {
        setSelectionSortOrder((prev) => {
            const newSortOrder = prev === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
            if (hasLoadOptions) {
                loadOptionsDebounced(selectionFilter, newSortOrder);
            }
            return newSortOrder;
        });
    });
    const toggleSelectedSortOrder = useCallbackRef(() => {
        setSelectedSortOrder((prev) => (prev === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC));
    });
    useEffect(() => {
        if (hasLoadOptions) {
            loadOptionsDebounced('');
        }
    }, [loadOptionsDebounced, hasLoadOptions]);
    return (_jsxs(Wrapper, { ref: ref, "data-testid": dataTestId, children: [_jsxs("div", { children: [_jsx(Search, { mode: 'selection', onDebouncedChange: handleSelectionFilterChange, onClear: clearSelectionFilter, disabled: isSelectionFilterDisabled, isInlineMode: isInlineMode, toggleInlineMode: toggleInlineMode, hideInlineToggle: hideInlineToggle, disableInlineToggle: disabled, translations: mergedTranslations, hasLoadOptions: hasLoadOptions }), isInlineMode ? (_jsx(StyledTextarea, { value: inlineValue, onChange: handleChangeStringValue, height: textareaHeight, disabled: disabled, "data-testid": 'inline-textarea' })) : (_jsx(List, { mode: reverseSelectionMode ? 'selected' : 'selection', options: filteredSelectionItems, total: availableSelectionItems.length, singleAction: selectItem, bulkAction: selectAll, hideBulkActions: hideBulkSelect, isLoading: isLoading || internalLoading, disabled: disabled || isMaxReached || selectableSelectionItems.length === 0, sortOrder: selectionSortOrder, toggleSortOrder: toggleSelectionSortOrder, containerHeight: listContainerHeight, getOptionKey: getOptionKey, formatOptionLabel: formatOptionLabel, translations: mergedTranslations, wrapperRef: selectionListRef }))] }), _jsxs("div", { children: [_jsx(Search, { mode: 'selected', onDebouncedChange: handleSelectedFilterChange, onClear: clearSelectedFilter, disabled: disabled, translations: mergedTranslations, hasLoadOptions: false }), _jsx(List, { mode: reverseSelectionMode ? 'selection' : 'selected', options: filteredSelectedItems, total: selectedValue.length, singleAction: unselectItem, bulkAction: unselectAll, hideBulkActions: hideBulkDeselect, disabled: disabled || filteredSelectedItems.length === 0, error: error, sortOrder: selectedSortOrder, toggleSortOrder: toggleSelectedSortOrder, containerHeight: listContainerHeight, getOptionKey: getOptionKey, formatOptionLabel: formatOptionLabel, translations: mergedTranslations })] })] }));
};
export const MultiSelection = memo(forwardRef(_MultiSelection));
